import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Avatar,
  Box,
  RadioGroup,
  Radio,
  VStack,
  Input,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { UserSwitch, Clock, Calendar } from 'phosphor-react';

export type DuracaoTipo = 'horas' | 'data';

export interface DuracaoConfig {
  tipo: DuracaoTipo;
  horas?: number;
  dataExpiracao?: string;
}

interface Usuario {
  id: number;
  nome: string;
  email?: string;
  imagem_url?: string;
}

interface SolicitarAcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (duracaoConfig: DuracaoConfig) => Promise<void> | void;
  usuario: Usuario | null;
  isLoading?: boolean;
  isReenvio?: boolean;
}

const OPCOES_HORAS = [
  { value: '1', label: '1 hora' },
  { value: '2', label: '2 horas' },
  { value: '4', label: '4 horas' },
  { value: '8', label: '8 horas' },
  { value: '24', label: '1 dia' },
  { value: '48', label: '2 dias' },
  { value: '72', label: '3 dias' },
  { value: '168', label: '7 dias' },
];

export function SolicitarAcessoModal({
  isOpen,
  onClose,
  onConfirm,
  usuario,
  isLoading = false,
  isReenvio = false,
}: SolicitarAcessoModalProps) {
  const [duracaoTipo, setDuracaoTipo] = useState<DuracaoTipo>('horas');
  const [horasSelecionadas, setHorasSelecionadas] = useState<string>('168');
  const [dataExpiracao, setDataExpiracao] = useState<string>('');

  // Calcula a data mínima (hoje) e máxima (90 dias) para o calendário
  const hoje = new Date();
  const dataMinima = hoje.toISOString().split('T')[0];
  const dataMaxima = new Date(hoje.getTime() + 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const handleConfirm = () => {
    const duracao: DuracaoConfig = {
      tipo: duracaoTipo,
    };

    if (duracaoTipo === 'horas') {
      duracao.horas = parseInt(horasSelecionadas, 10);
    } else {
      duracao.dataExpiracao = dataExpiracao;
    }

    onConfirm(duracao);
  };

  const handleClose = () => {
    // Reset estado ao fechar
    setDuracaoTipo('horas');
    setHorasSelecionadas('168');
    setDataExpiracao('');
    onClose();
  };

  const isConfirmDisabled = duracaoTipo === 'data' && !dataExpiracao;

  if (!usuario) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <Flex alignItems="center" gap={2}>
            <UserSwitch size={24} weight="bold" />
            <Text>{isReenvio ? 'Reenviar solicitação' : 'Solicitar acesso'}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Info do usuário */}
          <Box p={4} mb={4} borderWidth={1} borderRadius="lg" borderColor="gray.200" bg="gray.50">
            <Flex align="center" gap={3}>
              <Avatar size="md" name={usuario.nome} src={usuario.imagem_url} />
              <Box>
                <Text fontWeight="bold">{usuario.nome}</Text>
                {usuario.email && (
                  <Text fontSize="sm" color="gray.500">
                    {usuario.email}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>

          {isReenvio && (
            <Box
              p={3}
              mb={4}
              bg="yellow.50"
              borderRadius="md"
              border="1px solid"
              borderColor="yellow.200"
            >
              <Text fontSize="sm" color="yellow.700">
                Já existe uma solicitação pendente. Ao reenviar, o usuário receberá uma nova
                notificação.
              </Text>
            </Box>
          )}

          <Text mb={4} color="gray.600">
            Selecione por quanto tempo deseja ter acesso à conta:
          </Text>

          {/* Seleção do tipo de duração */}
          <RadioGroup
            value={duracaoTipo}
            onChange={value => setDuracaoTipo(value as DuracaoTipo)}
            mb={4}
          >
            <VStack align="start" spacing={3}>
              <Radio value="horas">
                <Flex align="center" gap={2}>
                  <Clock size={18} />
                  <Text>Duração em horas/dias</Text>
                </Flex>
              </Radio>
              <Radio value="data">
                <Flex align="center" gap={2}>
                  <Calendar size={18} />
                  <Text>Até uma data específica</Text>
                </Flex>
              </Radio>
            </VStack>
          </RadioGroup>

          {/* Opções baseadas no tipo selecionado */}
          {duracaoTipo === 'horas' ? (
            <Box p={4} bg="blue.50" borderRadius="md">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">
                  Selecione a duração:
                </FormLabel>
                <RadioGroup value={horasSelecionadas} onChange={setHorasSelecionadas}>
                  <Flex flexWrap="wrap" gap={2}>
                    {OPCOES_HORAS.map(opcao => (
                      <Box
                        key={opcao.value}
                        as="label"
                        cursor="pointer"
                        px={3}
                        py={2}
                        borderRadius="md"
                        bg={horasSelecionadas === opcao.value ? 'blue.500' : 'white'}
                        color={horasSelecionadas === opcao.value ? 'white' : 'gray.700'}
                        border="1px solid"
                        borderColor={horasSelecionadas === opcao.value ? 'blue.500' : 'gray.200'}
                        _hover={{
                          borderColor: 'blue.400',
                        }}
                        transition="all 0.2s"
                      >
                        <Radio value={opcao.value} display="none" />
                        <Text fontSize="sm" fontWeight="medium">
                          {opcao.label}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </RadioGroup>
              </FormControl>
            </Box>
          ) : (
            <Box p={4} bg="purple.50" borderRadius="md">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">
                  Selecione a data de expiração:
                </FormLabel>
                <Input
                  type="date"
                  value={dataExpiracao}
                  onChange={e => setDataExpiracao(e.target.value)}
                  min={dataMinima}
                  max={dataMaxima}
                  bg="white"
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  O acesso será válido até 23:59 da data selecionada (máximo 90 dias)
                </Text>
              </FormControl>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isLoading={isLoading}
            isDisabled={isConfirmDisabled}
          >
            {isReenvio ? 'Reenviar solicitação' : 'Solicitar acesso'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
